/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, March 12th 2022, 9:55:52 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import {
  TaskTemplate,
  TaskTemplateValidationSchema,
  DigitalSigningSettings,
  DigitalSigningSettingsValidationSchema,
  IUserTaskTypesData,
  DownstreamProcessConfiguration,
  DownstreamProcessConfigurationValidationSchema,
  EventCode,
  EventCodeValidationSchema,
  EventConfiguration,
  EventConfigurationValidationSchema,
  BoundaryEventConfiguration,
  BoundaryEventConfigurationValidationSchema,
} from "lib/schemas";
import { APISERVICEPATHS, useAPIMutation, useAPIForm, useAPIRequest } from "lib/api";
import { PrimaryKeyValue } from "lib/types";
import { useWorkflowStore } from "service-builder/stores";
import { useDeleteOSItem } from "version-control/services/DeleteOSItemAPI";

const taskDefinitionsAPIURL = APISERVICEPATHS.taskDefinitions;

export const useAPIMutateTaskTemplate = () => useAPIMutation<TaskTemplate>({ apiServicePath: taskDefinitionsAPIURL });

export const useTaskTemplateAPIForm = (
  taskTemplateFetchId: PrimaryKeyValue<TaskTemplate>,
  taskTemplate: TaskTemplate,
  updateTaskTemplateFromAPI: (object: TaskTemplate) => void
) => {
  const useAPIFormReturn = useAPIForm({
    apiFormDefaultValues: taskTemplate,
    apiFormDataValidationSchema: TaskTemplateValidationSchema,
    apiInitialFetchId: taskTemplateFetchId,
    apiServicePath: taskDefinitionsAPIURL,
    setAPIFormResponse: updateTaskTemplateFromAPI,
    primaryKey: "id",
    formResetDependencyArray: [taskTemplate?.sequence, taskTemplate?.next],
  });

  return useAPIFormReturn;
};

export const useDeleteTaskTemplate = () => useDeleteOSItem(taskDefinitionsAPIURL);

export const useDisableTaskTemplate = () => {
  const { patchData, ...otherProperties } = useAPIMutation<TaskTemplate>({ apiServicePath: taskDefinitionsAPIURL });

  const disableTaskTemplate = (
    taskTemplateId: PrimaryKeyValue<TaskTemplate>,
    options?: { onDisable: (taskTemplate: TaskTemplate) => void; apiQueryParams?: any }
  ) => {
    patchData({
      apiPathParams: [taskTemplateId, "disable"],
      apiQueryParams: options?.apiQueryParams,
      setValidAPIResponse: options?.onDisable,
    });
  };

  return { disableTaskTemplate, ...otherProperties };
};

const taskDefinitionsReorderAPIURL = APISERVICEPATHS.taskDefinitionsReorder;

export const useAPIReorderTaskTemplate = () =>
  useAPIMutation<TaskTemplate>({ apiServicePath: taskDefinitionsReorderAPIURL });

const digitalSigningSettingsAPIPath = APISERVICEPATHS.digitalSigningSettings;
export const useDigitalSigninSettingsAPI = () => {
  return {
    useList: () =>
      useAPIRequest<DigitalSigningSettings[]>({
        apiServicePath: digitalSigningSettingsAPIPath,
        makeRequestOnInitialMount: false,
      }),
    useMutate: () =>
      useAPIMutation<DigitalSigningSettings>({
        apiServicePath: digitalSigningSettingsAPIPath,
      }),
    useAPIForm: (args: {
      id: DigitalSigningSettings["id"];
      defaultValues?: DigitalSigningSettings;
      onAPISave: (a: DigitalSigningSettings) => void;
    }) => {
      return useAPIForm({
        apiServicePath: digitalSigningSettingsAPIPath,
        apiFormDataValidationSchema: DigitalSigningSettingsValidationSchema,
        apiFormDefaultValues: args?.defaultValues,
        apiInitialFetchId: args?.id,
        primaryKey: "id",
        setAPIFormResponse: args?.onAPISave,
        method: args?.id ? "PATCH" : "POST",
      });
    },
    useDelete: () => useDeleteOSItem<DigitalSigningSettings>(digitalSigningSettingsAPIPath),
  };
};

export const useAPIGetUserTaskTypes = () => {
  const { makeRequest, ...rest } = useAPIRequest<IUserTaskTypesData>({
    apiServicePath: APISERVICEPATHS.userTaskTypes,
    makeRequestOnInitialMount: false,
  });

  const { userTaskTypes, setUserTaskTypes } = useWorkflowStore();

  const loadUserTaskTypesFromAPI = React.useCallback(() => {
    if (Object.keys(userTaskTypes).length) return;
    makeRequest({ setValidAPIResponse: setUserTaskTypes });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(userTaskTypes)]);

  return { loadUserTaskTypesFromAPI, ...rest };
};

export const useDownstreamProcessConfigurationAPIForm = (options: {
  downstreamProcessConfiguration?: DownstreamProcessConfiguration;
  onUpdateDownstreamProcessConfiguration?: (object: DownstreamProcessConfiguration) => void;
  apiInitialFetchId?: DownstreamProcessConfiguration["id"];
}) => {
  const {
    downstreamProcessConfiguration = null,
    onUpdateDownstreamProcessConfiguration = () => {},
    apiInitialFetchId = null,
  } = options;

  const useAPIFormReturn = useAPIForm({
    apiFormDefaultValues: downstreamProcessConfiguration,
    apiFormDataValidationSchema: DownstreamProcessConfigurationValidationSchema,
    apiInitialFetchId,
    apiServicePath: APISERVICEPATHS.downstreamProcessConfigurations,
    setAPIFormResponse: onUpdateDownstreamProcessConfiguration,
    primaryKey: "id",
    formResetDependencyArray: [downstreamProcessConfiguration?.id],
  });

  return useAPIFormReturn;
};

export const useEventCodeAPIForm = (options: {
  eventCode?: EventCode;
  eventCodeId?: EventCode["id"];
  onUpdateEventCode?: (object: EventCode) => void;
  eventDirection: EventConfiguration["event_direction"];
}) => {
  const { eventCode = null, onUpdateEventCode = () => {}, eventCodeId = null, eventDirection } = options;

  const useAPIFormReturn = useAPIForm({
    apiFormDefaultValues: eventCode,
    apiFormDataValidationSchema: EventCodeValidationSchema,
    apiQueryParams: { event_direction: eventDirection },
    apiInitialFetchId: eventCodeId,
    apiServicePath: APISERVICEPATHS.eventCodes,
    setAPIFormResponse: onUpdateEventCode,
    primaryKey: "id",
  });

  return useAPIFormReturn;
};

export const useDeleteEventCode = () => useDeleteOSItem(APISERVICEPATHS.eventCodes);

export const useEventConfigurationAPIForm = (options: {
  eventConfiguration?: EventConfiguration;
  eventConfigurationId?: EventConfiguration["id"];
  onUpdateEventConfiguration?: (object: EventConfiguration) => void;
}) => {
  const { eventConfiguration = null, onUpdateEventConfiguration = () => {}, eventConfigurationId = null } = options;

  const useAPIFormReturn = useAPIForm({
    apiFormDefaultValues: eventConfiguration,
    apiFormDataValidationSchema: EventConfigurationValidationSchema,
    apiInitialFetchId: eventConfigurationId,
    apiServicePath: APISERVICEPATHS.eventConfigurations,
    setAPIFormResponse: onUpdateEventConfiguration,
    primaryKey: "id",
  });

  return useAPIFormReturn;
};

export const useDeleteEventConfiguration = () =>
  useDeleteOSItem<EventConfiguration>(APISERVICEPATHS.eventConfigurations);

export const useBoundaryEventConfigurationAPIForm = (options: {
  boundaryEventConfiguration?: BoundaryEventConfiguration;
  onUpdateBoundaryEventConfiguration?: (object: BoundaryEventConfiguration) => void;
}) => {
  const { boundaryEventConfiguration = null, onUpdateBoundaryEventConfiguration = () => {} } = options;

  const useAPIFormReturn = useAPIForm({
    apiFormDefaultValues: boundaryEventConfiguration,
    apiFormDataValidationSchema: BoundaryEventConfigurationValidationSchema,
    apiInitialFetchId: null,
    apiServicePath: APISERVICEPATHS.boundaryEventConfigurations,
    setAPIFormResponse: onUpdateBoundaryEventConfiguration,
    primaryKey: "id",
    formResetDependencyArray: [boundaryEventConfiguration?.id],
  });

  return useAPIFormReturn;
};

export const useDeleteBoundaryEvent = () => useDeleteOSItem(APISERVICEPATHS.boundaryEventConfigurations);
